import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarIcon,
  CheckCircle2,
  Loader2,
  PowerOff,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Button,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@sportscanner/ui";
import DatePicker from "./date-picker";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { closureBulkSchema } from "@sportscanner/schemas";
import { apiPost, getApiErrorCode } from "@/lib/api";
import {
  addDays as addDaysHelper,
  dateStrToEntry,
  toLocalDateStr,
} from "./helpers";
import RecurrenceField, {
  defaultRecurrence,
  type RecurrenceValue,
} from "./recurrence-field";

type ClosureConflictReason =
  | "ALREADY_CLOSED"
  | "BOOKING_IN_PAST"
  | "HAS_BOOKINGS";

interface BulkClosureResponse {
  seriesId: string;
  reason: string;
  created: Array<{ id: string; date: string; reason: string }>;
  conflicts: Array<{ date: string; reason: ClosureConflictReason }>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  onCreated?: () => void;
}

export default function ClubClosureDialog({
  open,
  onOpenChange,
  clubId,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<"form" | "result">("form");
  const [date, setDate] = useState<string>(toLocalDateStr(new Date()));
  const [reason, setReason] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceValue>(() =>
    defaultRecurrence(toLocalDateStr(new Date())),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkClosureResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep("form");
    setDate(toLocalDateStr(new Date()));
    setReason("");
    setIsRecurring(false);
    setRecurrence(defaultRecurrence(toLocalDateStr(new Date())));
    setResult(null);
    setIsSubmitting(false);
  }, [open]);

  const minDate = toLocalDateStr(new Date());
  const maxDate = useMemo(
    () => toLocalDateStr(addDaysHelper(new Date(), 90)),
    [],
  );

  const canSubmit = useMemo(() => {
    if (!reason.trim()) return false;
    if (isRecurring) {
      if (!recurrence.untilDate) return false;
      if (recurrence.frequency === "WEEKLY" && recurrence.weekdays.length === 0)
        return false;
    } else {
      if (!date) return false;
    }
    return !isSubmitting;
  }, [reason, isRecurring, recurrence, date, isSubmitting]);

  async function submit() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const body = {
        reason: reason.trim(),
        ...(isRecurring
          ? {
              recurrence:
                recurrence.frequency === "DAILY"
                  ? {
                      frequency: "DAILY",
                      startDate: date,
                      untilDate: recurrence.untilDate,
                    }
                  : {
                      frequency: "WEEKLY",
                      startDate: date,
                      untilDate: recurrence.untilDate,
                      weekdays: recurrence.weekdays,
                    },
            }
          : { date }),
      };
      const parsed = closureBulkSchema.safeParse(body);
      if (!parsed.success) {
        toast.error(
          t(
            `errors.api.${parsed.error.issues[0]?.message ?? "VALIDATION_FAILED"}`,
          ),
        );
        return;
      }
      const data = await apiPost<BulkClosureResponse>(
        `/api/admin/clubs/${clubId}/closures`,
        parsed.data,
      );
      setResult(data);
      setStep("result");
      onCreated?.();
    } catch (err) {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="md:max-w-md md:max-h-[90vh] md:overflow-y-auto">
        {step === "result" && result ? (
          <ClosureResultStep
            result={result}
            onMakeAnother={() => {
              setStep("form");
              setReason("");
              setResult(null);
            }}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PowerOff className="w-4 h-4 text-red-500" />
                {t("closures.form.title")}
              </DialogTitle>
              <p className="text-xs text-neutral-500 mt-1">
                {t("closures.form.subtitle")}
              </p>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-neutral-700">
                  {isRecurring
                    ? t("closures.form.firstDate")
                    : t("closures.form.date")}
                </Label>
                <DatePicker
                  value={date}
                  onChange={setDate}
                  min={minDate}
                  max={maxDate}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="closure-reason"
                  className="text-sm font-semibold text-neutral-700"
                >
                  {t("closures.form.reason")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="closure-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("closures.form.reasonPlaceholder")}
                  rows={2}
                  maxLength={200}
                  disabled={isSubmitting}
                />
              </div>

              <RecurrenceField
                enabled={isRecurring}
                onEnabledChange={setIsRecurring}
                value={recurrence}
                onChange={setRecurrence}
                startDate={date}
                disabled={isSubmitting}
              />

              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white py-3 h-auto disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("closures.form.creating")}
                  </>
                ) : (
                  <>
                    <PowerOff className="w-4 h-4 mr-2" />
                    {t("closures.form.submit")}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function ClosureResultStep({
  result,
  onMakeAnother,
  onClose,
}: {
  result: BulkClosureResponse;
  onMakeAnother: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { created, conflicts } = result;
  const allFailed = created.length === 0;

  function fmt(date: string) {
    const e = dateStrToEntry(date);
    return `${t(`days.long.${e.dayKey}`)}, ${e.date}. ${t(`months.${e.monthKey}`).slice(0, 3)}`;
  }

  return (
    <div className="space-y-5 py-2">
      <div className="flex justify-center">
        {allFailed ? (
          <div className="bg-red-100 rounded-full p-3">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
        ) : conflicts.length > 0 ? (
          <div className="bg-amber-100 rounded-full p-3">
            <AlertCircle className="w-10 h-10 text-amber-600" />
          </div>
        ) : (
          <CheckCircle2 className="w-14 h-14 text-emerald-500" />
        )}
      </div>

      <div className="text-center space-y-1">
        <h2 className="font-heading text-xl font-bold text-neutral-800">
          {allFailed
            ? t("closures.result.allFailedTitle")
            : conflicts.length > 0
              ? t("closures.result.partialTitle")
              : t("closures.result.successTitle")}
        </h2>
        <p className="text-sm text-neutral-500">
          {t("closures.result.summary", {
            created: created.length,
            conflicts: conflicts.length,
          })}
        </p>
      </div>

      {created.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <p className="font-heading font-semibold text-sm text-emerald-800 mb-1">
            {t("closures.result.createdHeading", { count: created.length })}
          </p>
          {created.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-1.5 text-sm text-neutral-700"
            >
              <CalendarIcon className="w-3.5 h-3.5 text-emerald-600" />
              {fmt(c.date)}
            </div>
          ))}
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <p className="font-heading font-semibold text-sm text-amber-800 mb-1">
            {t("closures.result.conflictsHeading", { count: conflicts.length })}
          </p>
          {conflicts.map((c, i) => (
            <div
              key={`${c.date}-${i}`}
              className="flex items-center justify-between gap-2 text-sm text-neutral-700"
            >
              <span>{fmt(c.date)}</span>
              <span className="text-xs text-neutral-500">
                {t(`closures.result.reason.${c.reason}`)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onMakeAnother}
          className="flex-1 rounded-full"
        >
          {t("closures.form.newOne")}
        </Button>
        <Button
          onClick={onClose}
          className="flex-1 rounded-full bg-primary-600 hover:bg-primary-700 text-white"
        >
          {t("closures.form.close")}
        </Button>
      </div>
    </div>
  );
}
